import { Box, CircularProgress, Typography } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { SingleOperators } from 'common/src/query-params/operators';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import {
  IAllocationTemplate,
  IMeter,
  IPage,
} from 'common/src/types/interfaces';

import { getActiveAllocationTemplate } from 'portal/src/api/AccountsService';
import { getMeters } from 'portal/src/api/MetersService';
import useAuth from 'portal/src/auth/useAuth';
import AllocationTemplateForm from 'portal/src/components//administration/Form/Accounts/Allocations/AllocationTemplateForm';
import forbiddenResponse from 'portal/src/navigation/responses/forbiddenResponse';

const AllocationTemplateView = () => {
  const { id: masterAccountId } = useParams();
  const { featureChecker } = useAuth();

  if (!featureChecker.productFeatureEnabled(ProductFeatures.allocations)) {
    throw forbiddenResponse();
  }

  const [
    { data: template, isFetching: isTemplateFetching, isError },
    { data: masterMetersPage, isFetching: isMasterMetersFetching },
  ] = useQueries({
    queries: [
      {
        queryKey: ['activeAllocationTemplate', masterAccountId],
        queryFn: () => getActiveAllocationTemplate(masterAccountId),
        enabled: !!masterAccountId,
      },
      {
        queryKey: ['activeMasterMeters', masterAccountId],
        queryFn: () =>
          getMeters({
            filters: [
              {
                field: 'parentAccountId',
                operator: SingleOperators.EQUALS,
                value: masterAccountId,
              },
              {
                field: 'isMaster',
                operator: SingleOperators.EQUALS,
                value: true,
              },
              {
                field: 'isActive',
                operator: SingleOperators.EQUALS,
                value: true,
              },
            ],
          }),
        initialData: { data: [] } as IPage<IMeter>,
        enabled: !!masterAccountId,
      },
    ],
  });

  const isFetching = isTemplateFetching || isMasterMetersFetching;

  if (isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        marginTop={12}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        marginTop={12}
      >
        <Typography variant="h6" paddingInline="20rem" textAlign="justify">
          An error occurred loading the allocation template, please try again
          later.
        </Typography>
      </Box>
    );
  }

  return (
    <AllocationTemplateForm
      masterAccountId={masterAccountId}
      masterMeters={masterMetersPage?.data ?? []}
      template={template ?? undefined}
    />
  );
};

export default AllocationTemplateView;
